import React, { useEffect, useMemo, useState } from 'react';
import ProductPicker from '../components/ProductPicker';
import ContactPicker from '../components/ContactPicker';
import LocalSearchSelect from '../components/LocalSearchSelect';
import { Plus, X, Search } from 'lucide-react';
import { purchaseApi } from '../api/purchase';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';

type Mode = 'cash' | 'bill';

const today = () => new Date().toISOString().split('T')[0];
const d10 = (v: any) => (v ? String(v).slice(0, 10) : '');
const blankItem = () => ({ id: null as any, product_id: '', location_id: '', qty: '1', unit_cost: '0', batch_no: '', expiry_date: '', has_expiry: false });
const blankHead = (branchId: any) => ({
  date: today(), branch_id: branchId ? String(branchId) : '', contact_id: '',
  paid_from_account_id: '', reference: '', supplier_invoice_no: '', due_date: '', notes: '',
});

/**
 * QuickBooks-style purchase entry (items only).
 *   mode="cash"  -> "Cash Purchase": paid from a cash/bank account now.
 *   mode="bill"  -> "Enter Bill": owed to a supplier (A/P), settled later via Pay Supplier.
 * Both can be re-opened from the search panel below the form and edited (stock + the
 * cash/A-P side are reversed and re-posted at the new total).
 */
const PurchaseEntry: React.FC<{ mode: Mode }> = ({ mode }) => {
  const isCash = mode === 'cash';
  const { showToast } = useToast();
  const { branchId } = useAuth();

  const [branches, setBranches] = useState<any[]>([]);
  const [locations, setLocations] = useState<any[]>([]);
  const [cashAccounts, setCashAccounts] = useState<any[]>([]);

  const [head, setHead] = useState<any>(blankHead(branchId));
  const [items, setItems] = useState<any[]>([blankItem()]);
  const [saving, setSaving] = useState(false);

  // ----- edit state -----
  const [editingId, setEditingId] = useState<number | null>(null); // bill: invoice id, cash: grn id
  const [editingNo, setEditingNo] = useState<string>('');
  const [recent, setRecent] = useState<any[]>([]);
  const [recentSearch, setRecentSearch] = useState('');
  const [loadingBill, setLoadingBill] = useState(false);

  useEffect(() => {
    purchaseApi.branches().then(r => setBranches(r.data.data || []));
    purchaseApi.locations().then(r => setLocations(r.data.data || []));
    purchaseApi.cashAccounts().then(r => setCashAccounts(r.data.data || [])).catch(() => {});
  }, []);

  const loadRecent = (search = recentSearch) => {
    const req = isCash ? purchaseApi.recentCash({ search }) : purchaseApi.supplierInvoices({ search });
    req.then(r => setRecent(r.data.data || [])).catch(() => setRecent([]));
  };
  // load recent list on mount + debounce on search change
  useEffect(() => {
    const t = setTimeout(() => loadRecent(recentSearch), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentSearch, isCash]);

  const setH = (f: string, v: string) => setHead((h: any) => ({ ...h, [f]: v }));
  const upItem = (i: number, fields: Record<string, any>) => setItems(l => l.map((x, idx) => idx === i ? { ...x, ...fields } : x));

  const resetForm = () => {
    setEditingId(null); setEditingNo('');
    setItems([blankItem()]);
    setHead(blankHead(branchId));
  };

  const voidEntry = async (id: number, no: string) => {
    if (!window.confirm(`Void ${no || 'this record'}? Stock and the ${isCash ? 'cash' : 'A/P'} postings will be reversed. This cannot be undone.`)) return;
    try {
      const res = isCash ? await purchaseApi.voidCash(id) : await purchaseApi.voidBill(id);
      showToast(res.data?.message || 'Voided.');
      if (editingId === id) resetForm();
      loadRecent();
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Could not void', 'error');
    }
  };

  const loadEntry = async (id: number) => {
    setLoadingBill(true);
    try {
      const r = isCash ? await purchaseApi.getCash(id) : await purchaseApi.getBill(id);
      const inv = r.data.data || {};
      if (!isCash && !inv.grn_id) {
        showToast('This bill has no linked goods receipt and can’t be edited here.', 'error');
        return;
      }
      setHead({
        date: d10(inv.invoice_date || inv.grn_date) || today(),
        branch_id: inv.branch_id != null ? String(inv.branch_id) : '',
        contact_id: inv.contact_id != null ? String(inv.contact_id) : '',
        contact_label: inv.supplier_name || '',
        paid_from_account_id: inv.paid_from_account_id != null ? String(inv.paid_from_account_id) : '',
        reference: inv.reference || '',
        supplier_invoice_no: inv.supplier_invoice_no || '',
        due_date: d10(inv.due_date),
        notes: inv.notes || '',
      });
      setItems((inv.items || []).length ? inv.items.map((it: any) => ({
        id: it.id ?? null,
        product_id: String(it.product_id),
        product_label: it.product_name ? (it.sku ? it.sku + ' · ' + it.product_name : it.product_name) : '',
        location_id: String(it.location_id),
        qty: String(it.qty),
        unit_cost: String(it.unit_cost),
        batch_no: it.batch_no || '',
        expiry_date: d10(it.expiry_date),
        has_expiry: !!it.has_expiry,
      })) : [blankItem()]);
      setEditingId(id);
      setEditingNo(inv.invoice_no || inv.grn_no || '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      showToast('Could not load that record.', 'error');
    } finally { setLoadingBill(false); }
  };

  const locOptions = useMemo(
    () => locations.filter((l: any) => !head.branch_id || String(l.branch_id) === String(head.branch_id)),
    [locations, head.branch_id]
  );
  const cashOptions = useMemo(
    () => cashAccounts.map((a: any) => ({ value: a.id, label: a.account_code + ' · ' + a.account_name })),
    [cashAccounts]
  );

  const grand = items.reduce((s, l) => s + (Number(l.qty) * Number(l.unit_cost) || 0), 0);

  const save = async () => {
    const cleanItems = items.filter(l => l.product_id && l.location_id && Number(l.qty) > 0)
      .map(l => ({ id: l.id ? Number(l.id) : undefined, product_id: Number(l.product_id), location_id: Number(l.location_id), qty: Number(l.qty), unit_cost: Number(l.unit_cost || 0), batch_no: l.batch_no || null, expiry_date: l.expiry_date || null }));

    if (!head.branch_id) { showToast('Branch is required', 'error'); return; }
    if (isCash && !head.paid_from_account_id) { showToast('Choose the account you are paying from', 'error'); return; }
    if (!isCash && !head.contact_id) { showToast('Supplier is required for a bill', 'error'); return; }
    if (cleanItems.length === 0) { showToast('Add at least one item', 'error'); return; }

    const payload: any = {
      date: head.date, branch_id: Number(head.branch_id),
      contact_id: head.contact_id ? Number(head.contact_id) : null,
      notes: head.notes || null, items: cleanItems,
    };
    if (isCash) { payload.paid_from_account_id = Number(head.paid_from_account_id); payload.reference = head.reference || null; }
    else { payload.supplier_invoice_no = head.supplier_invoice_no || null; payload.due_date = head.due_date || null; }

    setSaving(true);
    try {
      let res;
      if (editingId) {
        res = isCash ? await purchaseApi.updateCash(editingId, payload) : await purchaseApi.updateBill(editingId, payload);
        showToast(res.data.message || 'Updated.');
        resetForm();
        loadRecent();
      } else {
        res = isCash ? await purchaseApi.cashPurchase(payload) : await purchaseApi.enterBill(payload);
        const d = res.data.data || {};
        const ref = [d.grn, d.bill].filter(Boolean).join(' · ');
        showToast((res.data.message || 'Saved.') + (ref ? ' (' + ref + ')' : ''));
        setItems([blankItem()]);
        setHead((h: any) => ({ ...h, contact_id: '', reference: '', supplier_invoice_no: '', due_date: '', notes: '' }));
        loadRecent();
      }
    } catch (err: any) {
      showToast(err.response?.data?.message || 'Failed to save', 'error');
    } finally { setSaving(false); }
  };

  const th: React.CSSProperties = { padding: '8px 10px', fontSize: 10, fontWeight: 600, color: 'var(--tx3)', textTransform: 'uppercase' };
  const thR: React.CSSProperties = { ...th, textAlign: 'right' };

  const rowNo = (b: any) => b.invoice_no || b.grn_no || '';
  const rowAmt = (b: any) => Number(b.invoice_amount ?? b.amount ?? 0);
  const rowDate = (b: any) => b.invoice_date || b.grn_date;

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">
          {editingId
            ? (isCash ? `Editing Cash Purchase ${editingNo}` : `Editing Bill ${editingNo}`)
            : (isCash ? 'Cash Purchase' : 'Enter Bill')}
        </h1>
        <div style={{ fontSize: 12, color: 'var(--tx3)' }}>
          {editingId
            ? 'Change lines or details — stock and the money side are re-posted on save'
            : (isCash ? 'Buy and pay now — no bill, no payment step' : 'Record what you owe a supplier — pay later from Pay Supplier')}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">
          <span>{isCash ? (editingId ? 'Edit Cash Purchase' : 'Cash Purchase') : (editingId ? 'Edit Bill' : 'Bill')}</span>
          {editingId && <button className="btn btn-secondary btn-sm" onClick={resetForm}><X size={12} /> Cancel edit</button>}
        </div>
        <div className="card-body" style={{ opacity: loadingBill ? 0.5 : 1 }}>
          {/* ----- header band ----- */}
          <div className="form-grid-3" style={{ marginBottom: 6 }}>
            {isCash && (
              <div className="form-group"><label className="form-label">Paid from *</label>
                <LocalSearchSelect options={cashOptions} value={head.paid_from_account_id}
                  placeholder="Cash / bank account" onSelect={(v) => setH('paid_from_account_id', String(v))} />
              </div>
            )}
            <div className="form-group"><label className="form-label">{isCash ? 'Payee / Supplier' : 'Supplier *'}</label>
              <ContactPicker value={head.contact_id} placeholder="Type to search supplier"
                initialLabel={head.contact_label || ''}
                onSelect={(c: any) => { setH('contact_id', String(c.id)); setH('contact_label', c.name || ''); }} />
            </div>
            <div className="form-group"><label className="form-label">{isCash ? 'Date' : 'Bill date'} *</label>
              <input type="date" className="form-control" value={head.date} max={today()} onChange={e => setH('date', e.target.value)} />
            </div>
            <div className="form-group"><label className="form-label">Branch *</label>
              <select className="form-control" value={head.branch_id} onChange={e => setH('branch_id', e.target.value)}>
                <option value="">Select branch</option>
                {branches.map((b: any) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            {isCash ? (
              <div className="form-group"><label className="form-label">Ref / Cheque No</label>
                <input className="form-control" value={head.reference} onChange={e => setH('reference', e.target.value)} />
              </div>
            ) : (<>
              <div className="form-group"><label className="form-label">Bill No (supplier's)</label>
                <input className="form-control" value={head.supplier_invoice_no} onChange={e => setH('supplier_invoice_no', e.target.value)} />
              </div>
              <div className="form-group"><label className="form-label">Due date</label>
                <input type="date" className="form-control" value={head.due_date} onChange={e => setH('due_date', e.target.value)} />
              </div>
            </>)}
            <div className="form-group"><label className="form-label">Memo</label>
              <input className="form-control" value={head.notes} onChange={e => setH('notes', e.target.value)} />
            </div>
          </div>

          {/* ----- items ----- */}
          <table style={{ width: '100%', borderCollapse: 'collapse', margin: '10px 0 8px' }}>
            <thead><tr style={{ background: 'var(--sur2)' }}>
              <th style={th}>Item</th><th style={th}>Location</th><th style={thR}>Qty</th><th style={thR}>Unit Cost</th><th style={thR}>Total</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((line, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--bdr)' }}>
                  <td style={{ padding: '6px 4px' }}>
                    <ProductPicker value={line.product_id} fontSize={12} locationId={line.location_id || undefined}
                      initialLabel={line.product_label || ''}
                      onSelect={(p: any) => upItem(i, { product_id: String(p.id), product_label: p.sku ? p.sku + ' · ' + p.name : p.name, has_expiry: !!p.has_expiry, unit_cost: p.cost_price != null ? String(p.cost_price) : line.unit_cost })} />
                    {line.has_expiry && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <input className="form-control" style={{ fontSize: 11, flex: 1 }} placeholder="Batch no" value={line.batch_no} onChange={e => upItem(i, { batch_no: e.target.value })} />
                        <input type="date" className="form-control" style={{ fontSize: 11, width: 130 }} title="Expiry date" value={line.expiry_date} onChange={e => upItem(i, { expiry_date: e.target.value })} />
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '6px 4px', minWidth: 160 }}>
                    <select className="form-control" style={{ fontSize: 12 }} value={line.location_id} onChange={e => upItem(i, { location_id: e.target.value })}>
                      <option value="">Select location</option>
                      {locOptions.map((l: any) => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: '6px 4px', width: 80 }}><input className="form-control mono" type="number" value={line.qty} onChange={e => upItem(i, { qty: e.target.value })} style={{ textAlign: 'right' }} /></td>
                  <td style={{ padding: '6px 4px', width: 110 }}><input className="form-control mono" type="number" value={line.unit_cost} onChange={e => upItem(i, { unit_cost: e.target.value })} style={{ textAlign: 'right' }} /></td>
                  <td style={{ padding: '6px 4px', width: 120 }}>
                    <input className="form-control mono" type="number" inputMode="decimal" min={0} step="any"
                      title="Type the line total — it sets the unit cost"
                      style={{ textAlign: 'right', fontWeight: 600, border: '2px solid var(--blue)', background: 'var(--sur)', cursor: 'text' }}
                      value={line._tot !== undefined ? line._tot : (Number(line.qty) * Number(line.unit_cost) || 0).toFixed(2)}
                      onFocus={e => (e.target as HTMLInputElement).select()}
                      onChange={e => { const v = e.target.value; const q = Number(line.qty) || 0; upItem(i, { _tot: v, unit_cost: (q > 0 && v !== '') ? String(Math.round((Number(v) / q) * 10000) / 10000) : line.unit_cost }); }}
                      onBlur={() => upItem(i, { _tot: undefined })} />
                  </td>
                  <td><button onClick={() => setItems(l => l.length > 1 ? l.filter((_, idx) => idx !== i) : l)} style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 18 }}>×</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-secondary btn-sm" onClick={() => setItems(l => { const lastLoc = [...l].reverse().find(x => x.location_id)?.location_id || ''; return [...l, { ...blankItem(), location_id: lastLoc }]; })}><Plus size={12} /> Add Item</button>

          {/* ----- footer ----- */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginTop: 16, borderTop: '2px solid var(--bdr)', paddingTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'monospace', fontWeight: 700, fontSize: 16 }}>
              <span>Total:</span>
              <span style={{ minWidth: 120, textAlign: 'right' }}>{grand.toFixed(2)}</span>
            </div>
            {editingId && <button className="btn btn-secondary" onClick={resetForm} disabled={saving}>Cancel</button>}
            <button className={isCash ? 'btn btn-success' : 'btn btn-primary'} onClick={save} disabled={saving || loadingBill}>
              {saving ? 'Saving...' : (editingId ? (isCash ? 'Update Cash Purchase' : 'Update Bill') : (isCash ? 'Save Cash Purchase' : 'Save Bill'))}
            </button>
          </div>
        </div>
      </div>

      {/* ----- search / edit panel (under the form) ----- */}
      <div className="card">
        <div className="card-header"><span>{isCash ? 'Edit a past cash purchase' : 'Edit a past bill'}</span></div>
        <div className="card-body">
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--tx3)' }} />
            <input className="form-control" style={{ paddingLeft: 30 }}
              placeholder={isCash ? 'Search by receipt no or payee' : 'Search by bill no or supplier'}
              value={recentSearch} onChange={e => setRecentSearch(e.target.value)} />
          </div>
          <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: 'var(--sur2)' }}>
                <th style={th}>{isCash ? 'Receipt' : 'Bill'}</th>
                <th style={th}>{isCash ? 'Payee' : 'Supplier'}</th>
                <th style={thR}>Amount</th><th style={th}>Date</th><th></th>
              </tr></thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', padding: 20, color: 'var(--tx3)', fontSize: 12 }}>Nothing to edit yet.</td></tr>
                ) : recent.map((b: any) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid var(--bdr)', background: editingId === b.id ? 'var(--sur2)' : 'transparent' }}>
                    <td style={{ padding: '6px 10px', fontFamily: 'monospace', fontWeight: 600, fontSize: 12 }}>{rowNo(b)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 12 }}>{b.supplier_name || '—'}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{rowAmt(b).toFixed(2)}</td>
                    <td style={{ padding: '6px 10px', fontSize: 11, color: 'var(--tx3)' }}>{rowDate(b) ? new Date(rowDate(b)).toLocaleDateString() : ''}</td>
                    <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                      <button className="btn btn-secondary btn-sm" onClick={() => loadEntry(b.id)} disabled={loadingBill}>Correct</button>
                      <button className="btn btn-secondary btn-sm" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => voidEntry(b.id, rowNo(b))} disabled={loadingBill}>Void</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseEntry;
